import { Alert, Box } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { ShowMoreDialog } from "@poseidon/ui/components/ShowMoreDialog";
import { usePermissions } from "@poseidon/ui/components";
import { useSailorRiskProfile } from "../hooks/useSailorRiskProfile";

export function VulnerableSailorAlert({ sailor, riskProfile, sailorName }) {
  if (!sailor?.disabilitiesDescription && !riskProfile?.vulnerabilities?.length) {
    return null;
  }
  return (
    <Alert
      severity="warning"
      action={
        <ShowMoreDialog
          buttonText="Details"
          buttonVariant="text"
          title="Vulnerability Details"
          buttonProps={{
            size: "small",
            startIcon: <InfoIcon />,
          }}
        >
          {sailor.disabilitiesDescription && (
            <Box>Sailor declared: {sailor.disabilitiesDescription}</Box>
          )}
          {!!riskProfile?.vulnerabilities?.length && (
            <Box>Salt observed: {riskProfile.vulnerabilities.join(", ")}</Box>
          )}
        </ShowMoreDialog>
      }
    >
      Potentially Vulnerable Sailor{sailorName ? `: ${sailorName}` : ""}
    </Alert>
  );
}

function SailorFlag({ sailor }) {
  const { sailorRiskProfile } = useSailorRiskProfile({ sailorId: sailor.id });
  return (
    <VulnerableSailorAlert
      sailor={sailor}
      riskProfile={sailorRiskProfile}
      sailorName={`${sailor.firstName} ${sailor.lastName}`}
    />
  );
}

function SailorRefFlag({ sailorRef }) {
  const [sailorData] = useDocumentData(sailorRef);
  if (!sailorData) {
    return null;
  }
  return <SailorFlag sailor={{ id: sailorRef.id, ...sailorData }} />;
}

/**
 * Shows a "Potentially Vulnerable Sailor" alert for each sailor that has a
 * declared disability or observed vulnerabilities. Accepts either `sailors`
 * (already-loaded sailor objects with an `id`) or `sailorRefs` (Firestore
 * DocumentReferences, loaded on demand).
 *
 * Risk profiles are only readable with admin permissions, so nothing is
 * rendered for non-admin users (e.g. the dealer portal).
 */
export function SailorVulnerabilityFlags({ sailors, sailorRefs }) {
  const { canRead } = usePermissions();
  if (!canRead) {
    return null;
  }
  return (
    <>
      {(sailors ?? [])
        .filter((sailor) => sailor?.id)
        .map((sailor) => (
          <SailorFlag key={sailor.id} sailor={sailor} />
        ))}
      {(sailorRefs ?? [])
        .filter((sailorRef) => sailorRef?.id)
        .map((sailorRef) => (
          <SailorRefFlag key={sailorRef.id} sailorRef={sailorRef} />
        ))}
    </>
  );
}
